import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Shield, Lock, Mail, ArrowRight, Sparkles, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { TDP_HERO, LOCAL_FALLBACK } from '../config/tdpMedia';

/* ──────────────────────────────────────────────────────────────────────────
   DMK decorative ribbon
   ────────────────────────────────────────────────────────────────────────── */
const DMKRibbon = () => (
  <div className="tdp-flag-wave inline-flex h-1.5 w-44 overflow-hidden rounded-full shadow-md">
    <div className="flex-1 bg-[#E4002B]" />
    <div className="flex-1 bg-black flex items-center justify-center">
      <div className="h-1 w-1 rounded-full bg-white" />
    </div>
    <div className="flex-1 bg-[#E4002B]" />
  </div>
);

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await login(email, password);
      if (userData?.role === 'dial100') {
        navigate('/dial-100-incident-reporting');
      } else {
        navigate('/tamil-nadu-map'); // Main landing dashboard
      }
    } catch (err) {
      // toast handled inside AuthContext
    } finally {
      setLoading(false);
    }
  };

  /* Pre-computed particle layout so SSR & rerenders stay stable */
  const particles = useMemo(
    () =>
      Array.from({ length: 28 }).map((_, i) => ({
        id: i,
        left: `${(i * 7 + 2) % 100}%`,
        size: 3 + ((i * 5) % 6),
        delay: `${(i * 0.6) % 10}s`,
        duration: `${7 + ((i * 2) % 8)}s`,
        drift: `${((i % 2 === 0 ? 1 : -1) * (10 + (i * 5) % 40))}px`,
        colour: i % 3 === 0 ? '#E4002B' : i % 3 === 1 ? '#EF4444' : '#FFFFFF',
      })),
    []
  );

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden flex items-center justify-center p-4 sm:p-6"
      style={{
        background:
          'radial-gradient(circle at 15% 20%, #E4002B 0%, transparent 30%),' +
          'radial-gradient(circle at 85% 80%, #000000 0%, transparent 40%),' +
          'linear-gradient(135deg, #000000 0%, #18181b 35%, #27272a 65%, #09090b 100%)',
      }}
    >
      {/* ─── rising particles ─────────────────────────────────────────── */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {particles.map((p) => (
          <span
            key={p.id}
            className="tdp-particle absolute rounded-full"
            style={{
              left: p.left,
              bottom: '-10px',
              width: p.size,
              height: p.size,
              background: p.colour,
              boxShadow: `0 0 ${p.size * 2}px ${p.colour}`,
              '--particle-duration': p.duration,
              '--particle-delay': p.delay,
              '--particle-drift': p.drift,
              opacity: 0.55,
            }}
          />
        ))}
      </div>

      {/* ─── diagonal pinstripe overlay for depth ────────────────────── */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'repeating-linear-gradient(45deg, transparent 0, transparent 30px, rgba(255,255,255,0.5) 30px, rgba(255,255,255,0.5) 31px)',
        }}
      />

      {/* ─── two-column split: left portrait, right login ─────────────── */}
      <div className="relative z-10 w-full max-w-6xl">
        <div className="relative">
          {/* outer DMK glow */}
          <div className="absolute -inset-[1px] rounded-3xl bg-gradient-to-br from-red-500 via-zinc-950 to-red-800 opacity-80 blur-[2px]" aria-hidden="true" />

          <div className="relative grid grid-cols-1 lg:grid-cols-2 rounded-3xl overflow-hidden bg-white/95 backdrop-blur-xl border border-white/40 shadow-[0_25px_60px_-15px_rgba(220,38,38,0.3)]">

            {/* ───────── LEFT: Kanimozhi portrait panel ───────── */}
            <div
              className="relative hidden lg:flex flex-col justify-between p-10 text-white overflow-hidden min-h-[640px]"
              style={{
                background:
                  'radial-gradient(circle at 20% 20%, rgba(228,0,43,0.4) 0%, transparent 45%),' +
                  'radial-gradient(circle at 80% 80%, rgba(39,39,42,0.6) 0%, transparent 50%),' +
                  'linear-gradient(135deg, #000000 0%, #18181b 45%, #27272a 100%)',
              }}
            >
              {/* top: ribbon + party mark */}
              <div className="relative z-10 flex items-center gap-3">
                <DMKRibbon />
                <span className="text-[10px] font-bold tracking-[0.32em] uppercase text-zinc-300">
                  DMK · Tamil Nadu
                </span>
              </div>

              {/* centre: large portrait */}
              <div className="relative z-10 flex flex-col items-center text-center">
                <div className="relative w-56 h-56 xl:w-64 xl:h-64 mb-6">
                  <div className="absolute inset-0 rounded-full overflow-hidden tdp-glow border-[4px] border-white/95 shadow-2xl">
                    <img
                      src={TDP_HERO.src}
                      alt={TDP_HERO.alt}
                      referrerPolicy="no-referrer"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        if (e.currentTarget.dataset.fallbackUsed) {
                          e.currentTarget.style.display = 'none';
                          return;
                        }
                        e.currentTarget.dataset.fallbackUsed = '1';
                        e.currentTarget.src = LOCAL_FALLBACK;
                      }}
                    />
                  </div>
                </div>

                <h1
                  className="text-4xl xl:text-5xl font-heading font-extrabold tracking-wider uppercase bg-clip-text text-transparent tdp-gradient-shimmer"
                  style={{
                    backgroundImage:
                      'linear-gradient(90deg, #FFFFFF 0%, #FFAAAA 25%, #FFFFFF 50%, #FFAAAA 75%, #FFFFFF 100%)',
                  }}
                >
                  BLURA SAGA
                </h1>
                <p className="mt-2 text-base text-white/95 font-semibold tracking-[0.18em] uppercase">
                  Kanimozhi Karunanidhi
                </p>
                <p className="mt-1 text-[11px] text-zinc-300 font-medium tracking-[0.32em] uppercase">
                  MP Thoothukudi · Tamil Nadu
                </p>
                <div className="mx-auto mt-4 h-[2px] w-32 origin-center bg-gradient-to-r from-transparent via-red-400 to-transparent tdp-underline-pulse" />
                <p className="mt-4 text-sm text-zinc-300 max-w-sm leading-relaxed">
                  Real-time social media and web intelligence for senior DMK leader and MP Kanimozhi Karunanidhi — mentions, sentiment, alerts &amp; grievances monitoring.
                </p>
              </div>

              {/* bottom: footer tagline */}
              <div className="relative z-10 flex items-center justify-center gap-2 text-[10px] text-zinc-400 font-semibold tracking-wider uppercase">
                <span>Dravida Munnetra Kazhagam</span>
                <span className="h-1 w-1 rounded-full bg-red-500/80" />
                <span>Tamil Nadu</span>
              </div>
            </div>

            {/* ───────── RIGHT: login form panel ───────── */}
            <div className="relative p-6 sm:p-10 lg:p-12 flex flex-col justify-center">
              {/* compact mobile-only hero (left panel is hidden on mobile) */}
              <div className="lg:hidden text-center mb-6">
                <div className="relative mx-auto mb-4 w-24 h-24">
                  <div className="absolute inset-0 rounded-full overflow-hidden tdp-glow border-[3px] border-white/95">
                    <img
                      src={TDP_HERO.src}
                      alt={TDP_HERO.alt}
                      referrerPolicy="no-referrer"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        if (e.currentTarget.dataset.fallbackUsed) {
                          e.currentTarget.style.display = 'none';
                          return;
                        }
                        e.currentTarget.dataset.fallbackUsed = '1';
                        e.currentTarget.src = LOCAL_FALLBACK;
                      }}
                    />
                  </div>
                </div>
                <h1 className="text-2xl font-heading font-extrabold tracking-wider uppercase text-zinc-950">
                  BLURA SAGA
                </h1>
                <p className="text-[11px] text-zinc-700/80 font-medium tracking-[0.25em] uppercase mt-0.5">
                  Kanimozhi Karunanidhi
                </p>
              </div>

              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-zinc-200">
                <div className="relative">
                  <div className="absolute -inset-1 rounded-xl bg-gradient-to-br from-red-400 to-red-800 blur-sm opacity-70" />
                  <div className="relative p-2.5 rounded-xl bg-gradient-to-br from-red-600 to-red-900 shadow-lg">
                    <Shield className="h-5 w-5 text-white" />
                  </div>
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-heading font-bold text-zinc-950">
                    Secure Command Access
                  </h2>
                  <p className="text-[11px] text-zinc-700/80 font-medium tracking-wide">
                    DMK Tamil Nadu · Authorised personnel only
                  </p>
                </div>
                <Sparkles className="ml-auto h-4 w-4 text-red-500 animate-pulse" />
              </div>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-zinc-800">
                    Email
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="admin@blurasaga.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                      data-testid="email-input"
                      className="h-12 pl-10 border-2 border-zinc-300 bg-zinc-50/50 focus:border-red-600 focus:ring-red-600/20 text-base rounded-lg"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider text-zinc-800">
                    Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      data-testid="password-input"
                      /* pr-11 keeps the typed value clear of the toggle */
                      className="h-12 pl-10 pr-11 border-2 border-zinc-300 bg-zinc-50/50 focus:border-red-600 focus:ring-red-600/20 text-base rounded-lg"
                    />
                    {/*
                      type="button" is load-bearing: inside a <form> a button
                      defaults to type="submit", so revealing the password
                      would submit the login instead.
                    */}
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      // Screen readers announce the action; sighted users get the same on hover.
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                      title={showPassword ? 'Hide password' : 'Show password'}
                      data-testid="password-toggle-btn"
                      // -translate-y-1/2 with top-1/2 keeps it centred as the field grows.
                      className="absolute right-1 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-md text-zinc-400 transition-colors hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600/40"
                    >
                      {showPassword
                        ? <EyeOff className="h-4 w-4" aria-hidden="true" />
                        : <Eye className="h-4 w-4" aria-hidden="true" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-btn"
                  className="group relative w-full h-12 overflow-hidden text-base font-extrabold uppercase tracking-wider text-white border-0 rounded-lg shadow-lg shadow-red-900/30 transition-all duration-200 hover:shadow-xl hover:shadow-red-900/40 active:scale-[0.985] disabled:opacity-75"
                  style={{
                    background:
                      'linear-gradient(90deg, #991b1b 0%, #b91c1c 35%, #ef4444 65%, #fca5a5 100%)',
                    backgroundSize: '200% 100%',
                    animation: 'tdpGradientShimmer 4s linear infinite',
                  }}
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {loading ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        Authenticating…
                      </>
                    ) : (
                      <>
                        <Shield className="h-4 w-4" />
                        Enter Command Centre
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </span>
                </Button>
              </form>

              {/* Three quick reassurance pills */}
              <div className="mt-5 grid grid-cols-3 gap-2 text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide">
                <div className="flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md bg-red-50 text-red-700 border border-red-100">
                  <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
                  Encrypted
                </div>
                <div className="flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md bg-zinc-50 text-zinc-800 border border-zinc-200">
                  <span className="h-1.5 w-1.5 rounded-full bg-zinc-500 animate-pulse" />
                  24×7 Watch
                </div>
                <div className="flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md bg-red-50/60 text-red-700 border border-red-100/60">
                  <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
                  Audit-Logged
                </div>
              </div>

              <p className="mt-6 text-center text-[10px] text-zinc-500">
                © {new Date().getFullYear()} BLURA SAGA · Secure intelligence platform
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
